import { type ReactElement } from 'react';
import { useTable, List, ShowButton } from '@refinedev/antd';
import type { HttpError } from '@refinedev/core';
import { Table, Space, List as AntList, Card, Grid } from 'antd';

import type { AdminCustomerOrder } from '@/types';

const { useBreakpoint } = Grid;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCents(cents: number | null): string {
  if (cents === null || cents === undefined) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Mobile card component
// ---------------------------------------------------------------------------

interface MobileCustomerOrderCardProps {
  record: AdminCustomerOrder;
}

function MobileCustomerOrderCard({
  record,
}: MobileCustomerOrderCardProps): ReactElement {
  return (
    <Card size="small" style={{ width: '100%' }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div>
          <strong>Order ID:</strong> {record.orderId.substring(0, 8)}...
        </div>
        <div>
          <strong>Product Type:</strong> {record.productType ?? '—'}
        </div>
        <div>
          <strong>Amount:</strong> {formatCents(record.amountPaid)}
        </div>
        <div>
          <strong>Order Date:</strong>{' '}
          {new Date(record.orderDate).toLocaleDateString()}
        </div>
        <Space size="small">
          <ShowButton hideText size="small" recordItemId={record.id} />
        </Space>
      </Space>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomerOrderList(): ReactElement {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { tableProps } = useTable<AdminCustomerOrder, HttpError>({
    resource: 'customer-orders',
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'orderDate', order: 'desc' }],
    },
  });

  return (
    <List>
      {isMobile ? (
        <AntList
          dataSource={tableProps.dataSource ? [...tableProps.dataSource] : []}
          pagination={
            tableProps.pagination && typeof tableProps.pagination === 'object'
              ? {
                  current: tableProps.pagination.current,
                  pageSize: tableProps.pagination.pageSize,
                  total: tableProps.pagination.total,
                  onChange: tableProps.pagination.onChange,
                }
              : false
          }
          renderItem={(record) => (
            <AntList.Item key={record.id} style={{ padding: '8px 0' }}>
              <MobileCustomerOrderCard record={record} />
            </AntList.Item>
          )}
        />
      ) : null}

      {!isMobile ? (
        <Table {...tableProps} rowKey="id" scroll={{ x: 900 }} size="small">
          <Table.Column<AdminCustomerOrder>
            dataIndex="customerId"
            title="Customer ID"
            render={(customerId: string) => (
              <a href={`/customers/show/${customerId}`}>
                {customerId.substring(0, 8)}...
              </a>
            )}
            width={140}
          />

          <Table.Column<AdminCustomerOrder>
            dataIndex="orderId"
            title="Order ID"
            render={(orderId: string) => (
              <a href={`/orders/show/${orderId}`}>
                {orderId.substring(0, 8)}...
              </a>
            )}
            width={140}
          />

          <Table.Column<AdminCustomerOrder>
            dataIndex="orderDate"
            title="Order Date"
            render={(value: string) => new Date(value).toLocaleDateString()}
            width={120}
          />

          <Table.Column<AdminCustomerOrder>
            dataIndex="productType"
            title="Product Type"
            render={(value: string | null) => value ?? '—'}
            width={140}
          />

          <Table.Column<AdminCustomerOrder>
            dataIndex="amountPaid"
            title="Amount"
            render={(value: number | null) => formatCents(value)}
            width={100}
            align="right"
          />

          <Table.Column<AdminCustomerOrder>
            dataIndex="currency"
            title="Currency"
            width={90}
          />

          <Table.Column<AdminCustomerOrder>
            title="Actions"
            fixed="right"
            width={80}
            render={(_: unknown, record: AdminCustomerOrder) => (
              <Space size="small">
                <ShowButton hideText size="small" recordItemId={record.id} />
              </Space>
            )}
          />
        </Table>
      ) : null}
    </List>
  );
}
